import React, { useState, useEffect } from "react";
import { Form, Typography, Modal, DatePicker, message } from "antd";
import { LoginWrapper } from "./style";
import loginImage from "../../assets/images/login-1.png";
import loginLogo from '../../assets/images/newlog.svg';
import { customBaseUrl, httpPost } from "../../store/http";
import { hideLoader, showLoader } from "../../utils/loader";
import PinModal from "../../components/Modal/Pin/PinModal";
import GlassCard from "../../components/premium/GlassCard";
import PremiumButton from "../../components/premium/PremiumButton";
import PremiumInput from "../../components/premium/PremiumInput";

const { Title, Text, Paragraph } = Typography;


const getIpAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    return "N/A";
  }
};


export default function Login() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [deviceId, setDeviceId] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [showDeviceVerification, setShowDeviceVerification] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');

  useEffect(() => {
    // Generate device ID
    setDeviceId(navigator.userAgent);

    const fetchIp = async () => {
      const ip = await getIpAddress();
      setIpAddress(ip);
    };
    fetchIp();
  }, []);

  const handleSuccessfulLogin = (res) => {
    const data = res?.data;
    const userObj = data?.user || data;
    const userType = userObj?.userType || data?.userType || res?.userType;

    if (userType === 'ADMIN') {
      const accessToken = data?.accessToken || userObj?.accessToken || res?.accessToken;
      const refreshToken = data?.refreshToken || userObj?.refreshToken || res?.refreshToken;

      localStorage.setItem("userDetails", JSON.stringify({ token: accessToken, user: userObj }));
      localStorage.setItem("token", accessToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      if (userObj?.userId) localStorage.setItem("userId", userObj?.userId);
      if (userObj?.phoneNumber) localStorage.setItem("phoneNumber", userObj?.phoneNumber);
      if (userObj?.email) localStorage.setItem("email", userObj?.email);

      const rawRoles = userObj?.roles || data?.roles || res?.roles || [];
      const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
      localStorage.setItem("roles", JSON.stringify(roles));

      const firstName = userObj?.firstName || data?.firstName || res?.firstName;
      const lastName = userObj?.lastName || data?.lastName || res?.lastName;
      const surname = userObj?.surname || data?.surname || res?.surname;

      if (firstName && (lastName || surname)) {
        localStorage.setItem("fullName", `${firstName} ${lastName || surname}`);
      }

      const isMarketerOnly = roles.includes('MARKETER') && roles.length === 1;
      window.location.href = isMarketerOnly ? "/campaigns" : "/dashboard";
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    showLoader();

    const payload = {
      loginId: values.username,
      password: values.password,
      deviceInfo: {
        deviceId,
        deviceName: navigator.platform,
        deviceType: "WEB",
        os: navigator.oscpu || navigator.platform,
        version: navigator.appVersion,
        userAgent: navigator.userAgent,
        ipAddress,
      },
    };

    setData({ emailOrPhoneNumber: values.username });

    try {
      const res = await httpPost(`${customBaseUrl.authUrl}/auth/staff/login`, payload);
      hideLoader();
      setLoading(false);

      if (res.success) {
        if (res.data.requiresDeviceVerification) {
          setVerificationToken(res.data.verificationToken);
          setShowDeviceVerification(true);
          message.info({
            content: 'New Device Detected: ' + res.message,
            className: 'premium-glass-message',
          });
        } else if (res.data.requiresOtp) {
          setShowOtp(true);
          message.info({
            content: '2FA Required: ' + res.message,
            className: 'premium-glass-message',
          });
        } else {
          handleSuccessfulLogin(res);
        }

      } else {
        message.error({
          content: "Login Failed: " + res.message,
          className: 'premium-glass-message',
        });
        if (res.message === "Account not Verified") {
          setTimeout(() => {
            window.location.href = `/activate_account?emailOrPhone=${values.username}`;
            sessionStorage.setItem("activation_request_trials", "6B86B273FF34FCE19D6B804EFF5A3F5747ADA4EAA22F1D49C01E52DDB7875B4B");
          }, 3000);
        }
      }
    } catch (error) {
      hideLoader();
      setLoading(false);
      message.error({
        content: "Error: Something went wrong. Please try again.",
        className: 'premium-glass-message',
      });
    }
  };

  const handleDeviceVerificationSubmit = async (values) => {
    setLoading(true);
    showLoader();
    const payload = {
      verificationToken,
      otp: values.otp,
      dateOfBirth: values.dateOfBirth.format('YYYY-MM-DD'),
    };
    const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/device-change/verify`, payload);
    hideLoader();
    setLoading(false);
    if (res?.success) {
      setShowDeviceVerification(false);
      handleSuccessfulLogin(res);
    } else {
      message.error({
        content: "Error: " + (res.message || "Device verification failed."),
        className: 'premium-glass-message',
      });
    }
  };

  const handleOtpSubmit = async () => {
    if (otp.length !== 6) {
      return message.warning({
        content: "Please enter a valid 6-digit OTP.",
        className: 'premium-glass-message',
      });
    }
    setLoading(true);
    showLoader();
    const payload = {
      identifier: data.emailOrPhoneNumber,
      otp,
      purpose: "LOGIN",
    };
    const res = await httpPost(`${customBaseUrl.authUrl}/auth/login/verify-otp`, payload);
    hideLoader();
    setLoading(false);
    if (res.success) {
      setShowOtp(false);
      handleSuccessfulLogin(res);
    } else {
      message.error({
        content: "Error: " + (res.message || "Invalid OTP. Please try again."),
        className: 'premium-glass-message',
      });
    }
  };


  return (
    <LoginWrapper>
      <div className="auth-container">
        <div className="auth-info">
          <div className="logo-container">
            <img src={loginLogo} alt="WayaBank Logo" />
          </div>
          <h1>WayaBank <br />Admin Panel.</h1>
          <p>The premium portal for managing business operations with elegance and efficiency.</p>
          <img src={loginImage} alt="Login Illustration" style={{ marginTop: '40px', width: '80%', maxWidth: '400px' }} />
        </div>

        <div className="auth-form-section">
          <GlassCard padding="48px" radius="24px">
            <Title level={2} style={{ marginBottom: '8px', fontWeight: 600 }}>Sign in</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '32px' }}>
              Enter your credentials to access the console
            </Text>

            <Form layout="vertical" onFinish={onFinish} autoComplete="off">
              <Form.Item label="Email or Phone" name="username" rules={[{ required: true, message: 'Required' }]}>
                <PremiumInput placeholder="admin@wayabank.com" />
              </Form.Item>

              <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Required' }]}>
                <PremiumInput.Password placeholder="••••••••" />
              </Form.Item>

              <div style={{ textAlign: 'right', marginBottom: '24px' }}>
                <a href="/forgot_password" style={{ color: 'var(--waya-red)', fontSize: '0.9rem', fontWeight: 500 }}>
                  Forgot password?
                </a>
              </div>

              <Form.Item>
                <PremiumButton type="primary" htmlType="submit" loading={loading} block style={{ height: '48px' }}>
                  Sign in
                </PremiumButton>
              </Form.Item>
            </Form>

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Text style={{ fontSize: '0.85rem' }}>
                Protected by WayaBank Security Systems
              </Text>
            </div>
          </GlassCard>
        </div>
      </div>

      {showDeviceVerification && (
        <Modal
          title={<Title level={4} style={{ margin: 0 }}>Verify New Device</Title>}
          visible={showDeviceVerification}
          onCancel={() => setShowDeviceVerification(false)}
          footer={null}
          centered
          width={440}
          bodyStyle={{ padding: '40px' }}
        >
          <Paragraph style={{ color: 'var(--text-muted)', marginBottom: '32px' }}>
            A new device was detected. Please enter the OTP and your Date of Birth to continue.
          </Paragraph>
          <Form onFinish={handleDeviceVerificationSubmit} layout="vertical">
            <Form.Item label="OTP" name="otp" rules={[{ required: true, message: "Required" }]}>
              <PremiumInput placeholder="••••••" />
            </Form.Item>
            <Form.Item label="Date of Birth" name="dateOfBirth" rules={[{ required: true, message: "Required" }]}>
              <DatePicker style={{ width: "100%", height: '48px', borderRadius: 'var(--radius-md)' }} />
            </Form.Item>
            <Form.Item style={{ marginTop: '32px' }}>
              <PremiumButton type="primary" htmlType="submit" loading={loading} block style={{ height: '48px' }}>
                Verify Device
              </PremiumButton>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {showOtp && (
        <PinModal
          showModal={showOtp}
          hideModal={setShowOtp}
          otp={otp}
          setOtp={setOtp}
          title="2FA Verification"
          description="Enter the 6-digit code sent to your device"
          buttonLabel="Verify"
          handleSubmit={handleOtpSubmit}
          numInputs={6}
          pinMode={true}
        />
      )}
    </LoginWrapper>
  );
}
